import React, { useState } from "react";
import {
  Box,
  Card,
  CardMedia,
  CardContent,
  Typography,
  Grid,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Business,
  TrackChanges,
  Edit,
  Delete,
} from "@mui/icons-material";
import { Client } from "./types";
import Organization_2 from "../../assets/Images/client/Organization_2.png";
import { AlertsProps } from "components/Alerts/data/Alerts.type";
import { _deleteClient } from "middlewares/ClientApi/client";
import AlertDialog from "components/AlertDialog";
import { ConfirmationMessages } from "constants/constant";
import SnackBar from "components/SnackBar/SnackBar";

interface ClientCardsProps {
  clients: Client[];
  onClientClick?: (client: Client) => void;
  onEditClick?: (client: Client) => void;
  onClientDeleted?: (clientId: number) => void;
  onSetTargetClick?: (client: Client) => void;
}

const ClientCards: React.FC<ClientCardsProps> = ({
  clients,
  onClientClick,
  onEditClick,
  onClientDeleted,
  onSetTargetClick,
}) => {
  const [openModal, setOpenModal] = useState(false);
  const [success, setSuccess] = useState<AlertsProps>({
    show: false,
    severity: "info",
    message: "",
  });
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);
  const [selectedClientName, setSelectedClientName] = useState<string>("");

  const handleSetTarget = (client: Client) => {
    onSetTargetClick?.(client);
  };

  const handleEdit = (client: Client) => {
    onEditClick?.(client);
  };

  const handleDelete = (
    clientId: number,
    e: React.MouseEvent,
    clientName?: string,
  ) => {
    e.stopPropagation();
    setSelectedClientId(clientId);
    setSelectedClientName(clientName ?? "");
    openDialog();
  };

  const openDialog = () => {
    setOpenModal(true);
  };

  const handleClose = () => {
    setOpenModal(false);
    setSelectedClientId(null);
    setSelectedClientName("");
  };

  const handleCloseSnackBar = (state: AlertsProps) => {
    setSuccess(state);
  };

  const deleteClient = async () => {
    if (selectedClientId === null) return;

    try {
      const res = await _deleteClient(selectedClientId);
      if (res) {
        onClientDeleted?.(selectedClientId);

        // Show success message
        handleCloseSnackBar({
          show: true,
          severity: "success",
          message: "Deleted Successfully!",
        });
      }
    } catch (err) {
      console.error("Error:", err);
      // Show error message
      handleCloseSnackBar({
        show: true,
        severity: "error",
        message: "Failed to delete Client",
      });
    }
  };
  const handleConfirm = async () => {
    await deleteClient(); // Wait for deletion to complete
    handleClose();
  };

  return (
    <>
      <Grid container spacing={2}>
        {clients.map((client) => (
          <Grid item xs={12} sm={6} md={2.4} key={client.clientId}>
            <Card
              onClick={() => onClientClick?.(client)}
              sx={{
                borderRadius: 2,
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                transition: "transform 0.2s, box-shadow 0.2s",
                cursor: "pointer",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                },
              }}
            >
              <CardMedia
                component="img"
                height="70"
                image={client.imageName && client.imageName.trim() !== "" ? client.imageName : Organization_2}
                alt={client.clientName}
                sx={{ objectFit: "cover" }}
              />
              <CardContent sx={{ p: 1.25, "&:last-child": { pb: 1.25 } }}>
                <Typography
                  variant="subtitle2"
                  fontWeight={600}
                  sx={{ mb: 0.75, fontSize: "0.875rem", lineHeight: 1.3 }}
                >
                  {client.clientName}
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    mb: 0.5,
                  }}
                >
                  <Business sx={{ fontSize: 14, color: "text.secondary" }} />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: "0.75rem", minWidth: 0 }}
                  >
                    {client.clientType}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.75,
                      ml: "auto",
                      flexShrink: 0,
                    }}
                  >
                    <Tooltip title="Set Target">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSetTarget(client);
                        }}
                        sx={{
                          padding: "2px",
                          color: "primary.main",
                          "&:hover": {
                            bgcolor: "primary.light",
                            color: "primary.dark",
                          },
                        }}
                      >
                        <TrackChanges sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        aria-label="Edit client"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(client);
                        }}
                        sx={{
                          padding: "2px",
                          color: "primary.main",
                          "&:hover": {
                            bgcolor: "primary.light",
                            color: "primary.dark",
                          },
                        }}
                      >
                        <Edit sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        aria-label="Delete client"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(client.clientId, e, client.clientName);
                        }}
                        sx={{
                          padding: "2px",
                          color: "primary.main",
                          "&:hover": {
                            bgcolor: "error.light",
                            color: "error.main",
                          },
                        }}
                      >
                        <Delete sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      <AlertDialog
        open={openModal}
        openDialog={openDialog}
        handleClose={handleClose}
        handleConfirm={handleConfirm}
        locationName={selectedClientName}
        confirmationMessage={ConfirmationMessages.clientConfirmation}
      />
      <SnackBar
        show={success.show}
        message={success.message}
        severity={success.severity}
        onclose={() =>
          setSuccess({ show: false, severity: "info", message: "" })
        }
      />
    </>
  );
};

export default ClientCards;
